import React, { useEffect, useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import { MdShortText } from "react-icons/md";
import { HiMiniUsers } from "react-icons/hi2";
import { useAuth } from "../../../../firebase/AuthProvider";
import {
    OwnerPerformanceRoleDto,
    OwnerEmployeePerformanceDto
} from "../../../../interfaces";
import {
    GetPerformanceEmployeeRolesAsync,
    GetPerformanceEmployeeCurrentAsync
} from "../../../../services/OwnerMetricsService/OwnerMetricsService";
import { Dropdown } from "react-bootstrap";
import './TopPerformers.css';
import { MultiUsersIcon } from "../../SvgIcons/MultiUsersIcon";
import { ThinSortIcon } from "../../SvgIcons/ThinSortIcon";
import AvatarImage from "../../../ui/Avatar/AvatarImage";
type SortByOption = "score" | "late" | "rejected" | "extended" | "hours" | "displayname";
type SortDirection = "asc" | "desc";

const SortOptionsArray: { key: SortByOption; value: string }[] = [
    { key: "score", value: "Sort by Score" },
    { key: "late", value: "Sort by Late" },
    { key: "rejected", value: "Sort by Rejected" },
    { key: "extended", value: "Sort by Extended" },
    { key: "hours", value: "Sort by Hours" },
    { key: "displayname", value: "Sort by Name" },
];


const TopPerformers: React.FC = () => {
    const { currentUser } = useAuth();

    const [roles, setRoles] = useState<OwnerPerformanceRoleDto[]>([]);
    const [selectedRole, setSelectedRole] = useState<string>("");
    const [performers, setPerformers] = useState<OwnerEmployeePerformanceDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);

    const [sortBy, setSortBy] = useState<SortByOption>("score");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [isSortDropdownOpen, setIsSortDropdownOpen] = useState<boolean>(false);
    const [isRolesDropdownOpen, setisRolesDropdownOpen] = useState<boolean>(false);
    const selectedSortLabel =
        SortOptionsArray.find(opt => opt.key === sortBy)?.value || "Sort";

    const ShortText = MdShortText as any;
    const MiniUsers = HiMiniUsers as any;


    useEffect(() => {
        fetchRoles();
    }, []);

    useEffect(() => {
        if (selectedRole) {
            fetchPerformers();
        }
    }, [selectedRole, page, pageSize, sortBy, sortDirection]);

    const fetchRoles = async () => {
        const token = await currentUser?.getIdToken();
        if (!token) return;

        setLoadingRoles(true);
        setError(null);

        try {
            const result = await GetPerformanceEmployeeRolesAsync(token);
            if (result.success && result.data) {
                setRoles(result.data);
                if (result.data.length > 0 && !selectedRole) {
                    setSelectedRole(result.data[0].role);
                }
            } else {
                toast.error(result.message || "Failed to load roles");
                setError(result.message || "Failed to load roles");
            }
        } catch (error: any) {
            const msg = error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : error.message || "Error loading roles";
            toast.error(msg);
            setError(msg);
        } finally {
            setLoadingRoles(false);
        }
    };

    const fetchPerformers = async () => {
        const token = await currentUser?.getIdToken();
        if (!token || !selectedRole) return;

        setLoading(true);
        setError(null);

        try {
            const result = await GetPerformanceEmployeeCurrentAsync(
                token,
                selectedRole,
                page,
                pageSize,
                sortBy,
                sortDirection
            );

            if (result.success && result.data) {
                setPerformers(result.data.items);
                const calculatedTotalPages = Math.ceil(result.data.total / result.data.pageSize);
                setTotalPages(calculatedTotalPages);
                setTotalItems(result.data.total);
            } else {
                toast.error(result.message || "Failed to load performers");
                setError(result.message || "Failed to load performers");
                setPerformers([]);
            }
        } catch (error: any) {
            const msg = error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : error.message || "Error loading performers";
            toast.error(msg);
            setError(msg);
            setPerformers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = (newRole: string) => {
        if (!newRole || newRole === "") return;

        setSelectedRole(newRole);
        setPage(1);
    };

    // const handleSortChange = (newSortBy: SortByOption) => {
    //     setSortBy(newSortBy);
    //     setPage(1);
    // };

    const handleSortChange = (key: SortByOption) => {
        setSortBy(key);
        setIsSortDropdownOpen(false);
    };


    const handleSortDirectionToggle = () => {
        setSortDirection(prev => prev === "asc" ? "desc" : "asc");
        setPage(1);
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(e.target.value, 10);
        setPageSize(newPageSize);
        setPage(1);
    };

    const truncateText = (
        text?: string,
        limit: number = 12
    ): string => {
        if (!text) return "";
        return text.length > limit ? `${text.slice(0, limit)}...` : text;
    };

    const fullText =
        selectedRole
            ? `${selectedRole}`
            : roles?.[0]?.role ?? "Sort";

    const getAvatarUrl = (employeeId: string, employeeName: string) => {
        try {
            const nameHash = employeeName && employeeName.trim()
                ? employeeName.split(' ').map(n => n[0]).join('').toLowerCase()
                : employeeId;
            const hashValue = nameHash && nameHash.length > 0
                ? nameHash.charCodeAt(0) % 70
                : parseInt(employeeId.slice(-2) || '1', 16) % 70;
            return `https://i.pravatar.cc/40?img=${hashValue}`;
        } catch {
            return `https://i.pravatar.cc/40?img=1`;
        }
    };

    return (
        <div className="ow_nw_box">
            <div className="ow_nw_box_header mb-3">
                <h3 className="text-[16px] xl:text-[18px] 2xl:text-[20px] font-semibold">Top performers</h3>

                <button
                    onClick={handleSortDirectionToggle}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition"
                >
                    {sortDirection === "asc" ? "Ascending" : "Descending"}
                    <ThinSortIcon size={14} />
                </button>
            </div>

            {/* {selectedRole && (
                <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginTop: "1rem"
                }}>
                    <Dropdown onToggle={(isOpen) => setIsSortDropdownOpen(isOpen)}>
                        <Dropdown.Toggle className="ow_nw_dropdown_with_icon" variant="light">
                            <div className="w-100 flex items-center justify-between text-base">
                                <span>Sort</span>
                                {
                                    isSortDropdownOpen ? (
                                        <FontAwesomeIcon color="#747474" icon={faAngleUp} />
                                    ) : (
                                        <FontAwesomeIcon color="#747474" icon={faAngleDown} />
                                    )
                                }
                            </div>
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {
                                SortOptionsArray && SortOptionsArray?.length > 0 ? (
                                    SortOptionsArray?.map((raw, index: number) => {
                                        return (
                                            <Dropdown.Item
                                                key={`key_${index + 1}`}
                                                onClick={() => handleSortChange(raw?.key)}
                                                style={{ color: "#747474" }}
                                            >
                                                {raw?.value || ""}
                                            </Dropdown.Item>
                                        )
                                    })
                                ) : (
                                    <Dropdown.Item style={{ color: "#747474" }}>
                                        No Options
                                    </Dropdown.Item>
                                )
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                    <button
                        onClick={handleSortDirectionToggle}
                        style={{
                            fontSize: '12px',
                            padding: '0.52rem 0.75rem',
                            border: '1px solid #c9c9c9',
                            borderRadius: '8px',
                            background: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.325rem',
                            color: "#747474"
                        }}
                    >
                        {sortDirection === "asc" ? "Ascending" : "Descending"}
                        <ThinSortIcon size={14} />
                    </button>
                </div>
            )} */}

            <div className="grid md:grid-cols-2 gap-2 mb-3">
                {loadingRoles ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span style={{ fontSize: '0.75rem' }}>Loading roles...</span>
                    </div>
                ) : (
                    <div className="relative inline-block w-full">
                        {/* Toggle Button */}
                        <button
                            onClick={() => setisRolesDropdownOpen((prev) => !prev)}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-gray-300 bg-white text-sm sm:text-base text-gray-700 hover:bg-gray-50 transition"
                        >
                            <div className="flex items-center gap-2 truncate">
                                <MiniUsers size={18} className="shrink-0 text-[#888888]" />
                                {/* <span className="truncate">
                                    {selectedRole
                                        ? <>{selectedRole} ({roles.find(r => r.role == selectedRole)?.activeCount})</>
                                        : roles && roles.length > 0
                                            ? roles[0]?.role
                                            : "Sort"}
                                </span> */}
                                <span className="whitespace-nowrap">
                                    {truncateText(fullText)}
                                </span>
                            </div>

                            <FontAwesomeIcon
                                icon={isRolesDropdownOpen ? faAngleUp : faAngleDown}
                                className="text-gray-500 text-sm"
                            />
                        </button>

                        {/* Dropdown Menu */}
                        {isRolesDropdownOpen && (
                            <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-60 overflow-auto">
                                {roles && roles.length > 0 ? (
                                    roles.map((role, index) => {
                                        const isSelected = selectedRole === role?.role;

                                        return (
                                            <button
                                                key={`role_${index + 1}`}
                                                onClick={() => {
                                                    handleRoleChange(role?.role);
                                                    setisRolesDropdownOpen(false);
                                                }}
                                                className={`w-full flex items-center justify-between ps-1.5 pr-1  py-2 transition
                ${isSelected
                                                        ? "bg-[#F9F6FF] text-[#7E57C2] font-medium"
                                                        : "text-gray-600 hover:bg-gray-100"
                                                    }`}
                                            >
                                                <span className="truncate text-xs">{role?.role || ""}</span>
                                                <span
                                                    className={`text-xs ${isSelected ? "text-[#7E57C2]" : "text-gray-500"
                                                        }`}
                                                >
                                                    ({role?.activeCount ?? "N/A"})
                                                </span>
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-400">
                                        No role available
                                    </div>
                                )}
                            </div>
                        )}
                    </div>


                )}

                {selectedRole && (
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        {/* Sort Dropdown */}
                        <div className="relative w-full">
                            {/* Button */}
                            <button
                                onClick={() => setIsSortDropdownOpen(prev => !prev)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-md
               border border-gray-300 bg-white text-sm sm:text-base
               text-gray-700 hover:bg-gray-50 transition"
                            >
                                <div className="flex items-center gap-2 truncate">
                                    <ShortText size={20} className="text-[#888888]" />
                                    <span className="truncate">{selectedSortLabel}</span>
                                </div>

                                <FontAwesomeIcon
                                    icon={isSortDropdownOpen ? faAngleUp : faAngleDown}
                                    className="text-gray-500 text-sm"
                                />
                            </button>

                            {/* Dropdown */}
                            {isSortDropdownOpen && (
                                <div className="absolute z-50 mt-1 w-full rounded-md border
                    border-gray-200 bg-white shadow-lg max-h-56 overflow-auto">
                                    {SortOptionsArray.map((raw, index) => {
                                        const isSelected = sortBy === raw.key;

                                        return (
                                            <button
                                                key={`sort_${index}`}
                                                onClick={() => handleSortChange(raw.key)}
                                                className={`w-full px-3 py-2 text-left text-xs transition
              ${isSelected
                                                        ? "bg-[#F9F6FF] text-[#7E57C2] font-medium"
                                                        : "text-gray-600 hover:bg-gray-100"
                                                    }`}
                                            >
                                                {raw.value}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                    </div>
                )}
            </div>


            {loading && (
                <div style={{
                    textAlign: 'center',
                    padding: '2.5rem',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '0.5rem' }} />
                    Loading performers...
                </div>
            )}

            {error && !loading && (
                <div style={{
                    textAlign: 'center',
                    padding: '2.5rem',
                    color: '#dc2626',
                    fontSize: '0.875rem'
                }}>
                    {error}
                </div>
            )}

            {!loading && !error && performers.length === 0 && selectedRole && (
                <div style={{
                    textAlign: 'center',
                    padding: '2.5rem',
                    color: '#6b7280',
                    fontSize: '0.875rem'
                }}>
                    No performers found for this role.
                </div>
            )}

            {!loading && !error && performers.length > 0 && (
                <>
                    <div className="h-[400px] overflow-auto no-scrollbar">
                        {performers.map((p) => (
                            <div
                                key={p.employeeId}
                                className="flex mb-2 items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-[10px] border"
                            >
                                {/* Avatar */}
                                {/* <img
                                    src={getAvatarUrl(p.employeeId, p?.displayName)}
                                    alt={p?.displayName}
                                    title={p?.displayName}
                                    className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
                                /> */}

                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0" style={{ cursor: "pointer" }}>
                                    <AvatarImage size="sm" userId={p.employeeId} />
                                </div>

                                {/* Info */}
                                <div className="flex flex-col gap-2 w-full">
                                    {/* Name */}
                                    <div className="capitalize text-sm sm:text-base font-semibold text-gray-900 truncate">
                                        {p?.displayName}
                                    </div>

                                    {/* Stats */}
                                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                        <span className="text-[10px] sm:text-xs px-2 py-0.5 font-medium rounded bg-indigo-100 text-indigo-700">
                                            SCORE: {p.score.toFixed(1)}
                                        </span>

                                        <span className="text-[10px] sm:text-xs px-2 py-0.5 font-medium rounded bg-yellow-100 text-yellow-700">
                                            LATE: {p.late}
                                        </span>

                                        <span className="text-[10px] sm:text-xs px-2 py-0.5 font-medium rounded bg-red-100 text-red-700">
                                            REJECTED: {p.rejected}
                                        </span>

                                        <span className="text-[10px] sm:text-xs px-2 py-0.5 font-medium rounded bg-blue-100 text-blue-700">
                                            EXTENDED: {p.extended}
                                        </span>

                                        <span className="text-[10px] sm:text-xs px-2 py-0.5 font-medium rounded bg-green-100 text-green-700">
                                            HRS: {p.hours.toFixed(1)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalItems > 0 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '1rem',
                            paddingTop: '0.75rem',
                            borderTop: '1px solid #f1f1f1',
                            flexWrap: 'wrap',
                            gap: '0.5rem'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Show:</span>
                                <select
                                    className="ow_common_dropdown"
                                    value={pageSize}
                                    onChange={handlePageSizeChange}
                                    style={{ fontSize: '0.6875rem', padding: '0.25rem 0.375rem' }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                </select>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Entries</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                    Page {page} of {totalPages} ({totalItems} total)
                                </span>
                                {totalPages > 1 && (
                                    <>
                                        <button
                                            onClick={() => handlePageChange(page - 1)}
                                            disabled={page === 1}
                                            style={{
                                                fontSize: '0.6875rem',
                                                padding: '0.25rem 0.5rem',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '0.375rem',
                                                background: page === 1 ? '#f9fafb' : '#fff',
                                                cursor: page === 1 ? 'not-allowed' : 'pointer',
                                                opacity: page === 1 ? 0.5 : 1
                                            }}
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                            style={{
                                                fontSize: '0.6875rem',
                                                padding: '0.25rem 0.5rem',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: '0.375rem',
                                                background: page === totalPages ? '#f9fafb' : '#fff',
                                                cursor: page === totalPages ? 'not-allowed' : 'pointer',
                                                opacity: page === totalPages ? 0.5 : 1
                                            }}
                                        >
                                            Next
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default TopPerformers;